import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Text, Stack, Inline } from '@atlaskit/primitives/compiled';
import { Field } from '@atlaskit/form';
import { token } from '@atlaskit/tokens';
import Range from '@atlaskit/range';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import type { CustomLengthType } from '../../../../types/customizationTypes';
import { CustomLength } from '../../../../types/customizationTypes';

const i18n = defineMessages({
	audioDurationLabel: {
		id: 'audio.audio-popup.customize-tab.duration-label',
		defaultMessage: 'Roughly how long is the audio?',
		description: 'Label for a Select component used for selecting audio duration',
	},
	shortDurationLabel: {
		id: 'audio.audio-popup.customize-tab.duration.short',
		defaultMessage: 'Short',
		description: 'Label for a shorter length audio option',
	},
	mediumDurationLabel: {
		id: 'audio.audio-popup.customize-tab.duration.medium',
		defaultMessage: 'Medium',
		description: 'Label for a medium length audio option',
	},
	longDurationLabel: {
		id: 'audio.audio-popup.customize-tab.duration.long',
		defaultMessage: 'Long',
		description: 'Label for a longer length audio option',
	},
});

interface DurationOption {
	label: keyof typeof i18n;
	value: CustomLengthType;
}

const durationOptions: DurationOption[] = [
	{
		label: 'shortDurationLabel',
		value: CustomLength.SHORT,
	},
	{
		label: 'mediumDurationLabel',
		value: CustomLength.MEDIUM,
	},
	{
		label: 'longDurationLabel',
		value: CustomLength.LONG,
	},
];

const styles = cssMap({
	durationSliderWrapper: {
		marginTop: token('space.negative.100'),
	},
	slider: {
		paddingInlineEnd: token('space.100'),
	},
});

export const DurationSelector = () => {
	const { formatMessage } = useIntl();
	const [{ customization }, { setCustomization }] = useAudioBriefing();
	const { length } = customization;

	return (
		<Field
			name="length"
			defaultValue={length}
			label={formatMessage(i18n.audioDurationLabel)}
			id="audio-duration-field"
		>
			{({ fieldProps: { value: customLength, onChange } }) => {
				const rangeValue = durationOptions.findIndex((option) => option.value === customLength);

				const handleRangeChange = (newRangeValue: number) => {
					// Map the float value to the nearest option index
					const index = Math.round(newRangeValue);
					const selectedOption = durationOptions[index].value;
					onChange(selectedOption);
					setCustomization({
						...customization,
						length: selectedOption,
					});
				};

				return (
					<Box xcss={styles.durationSliderWrapper}>
						<Stack grow="fill">
							<Box xcss={styles.slider}>
								<Range
									step={0.1}
									min={-0.1}
									max={2.0}
									value={rangeValue}
									onChange={handleRangeChange}
									testId="duration-range-slider"
									aria-labelledby="audio-duration-field-label"
								/>
							</Box>
							<Inline spread="space-between" grow="fill">
								{durationOptions.map((option, index) => (
									<Text
										key={option.value}
										size="small"
										color={rangeValue === index ? 'color.text' : 'color.text.subtlest'}
										weight={rangeValue === index ? 'bold' : 'regular'}
									>
										{formatMessage(i18n[option.label])}
									</Text>
								))}
							</Inline>
						</Stack>
					</Box>
				);
			}}
		</Field>
	);
};
